import { useContext } from "react";
import { Button, Col, Form, Row, Typography } from "antd";
import { Controller } from "react-hook-form";
import Dragger from "antd/es/upload/Dragger";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import {
    InboxOutlined,
    FilePdfOutlined,
    PictureOutlined,
    EyeOutlined,
} from "@ant-design/icons";

import { ICity, ICityTerms } from "../../../../../../interfaces";
import { ICityFormStepProps, IChangeFileProps } from "../../../../interfaces";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";

import useFilesStep from "./useFilesStep";
import FieldsGroupTitle from "../../../FieldsDivider";

const { Text } = Typography;

interface IProps extends ICityFormStepProps {
    colInputs: any;
    acceptedImageTypes: Array<string>;
    acceptedFileTypes: Array<string>;
    lightLogoImageUrl: string | ArrayBuffer | null | undefined;
    darkLogoImageUrl: string | ArrayBuffer | null | undefined;
    backgroundImageUrl: string | ArrayBuffer | null | undefined;
    logoCountyImageUrl: string | ArrayBuffer | null | undefined;
    termsOfUseFileName: string | undefined;
    raffleRulesFileName: string | undefined;
    uploadedRaffleRulesFile: string | undefined;
    onChangeLightLogoImage: (props: IChangeFileProps) => void;
    onChangeDarkLogoImage: (props: IChangeFileProps) => void;
    onChangeBackgroundImage: (props: IChangeFileProps) => void;
    onChangeLogoCountyImage: (props: IChangeFileProps) => void;
    onChangeTermsOfUseFile: (props: IChangeFileProps) => void;
    onChangeRaffleRulesFile: (props: IChangeFileProps) => void;
    hasTermsOfUse: boolean;
    hasRaffleRules: boolean;
    city: ICity | undefined;
    needsToInformTermsOfUseEffectiveDate: boolean;
    upcomingTerms: ICityTerms | null | undefined;
}

export default function FilesStep({
    errors,
    control,
    acceptedImageTypes,
    acceptedFileTypes,
    lightLogoImageUrl,
    darkLogoImageUrl,
    backgroundImageUrl,
    logoCountyImageUrl,
    raffleRulesFileName,
    uploadedRaffleRulesFile,
    onChangeLightLogoImage,
    onChangeDarkLogoImage,
    onChangeBackgroundImage,
    onChangeRaffleRulesFile,
    onChangeLogoCountyImage,
    city,
    needsToInformTermsOfUseEffectiveDate,
}: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const {
        handleClickViewRaffleRules,
        dummyRequest,
    } = useFilesStep(city, needsToInformTermsOfUseEffectiveDate);

    const draggerCardStyle = {
        background: isDark ? "#1f1f1f" : "#fbfcfd",
        border: `1px dashed ${isDark ? "#3a3a3a" : "#cbd5e1"}`,
        borderRadius: 8,
        padding: "16px",
    };

    return (
        <div>
            {/* Seção 1: Identidade Visual e Imagens */}
            <FieldsGroupTitle
                title="Logos e Identidade Visual"
                icon={<PictureOutlined style={{ color: "#1677ff" }} />}
                subtitle="Faça o upload dos logos da prefeitura e imagens para os temas claro e escuro do aplicativo."
            />

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label="Logo da Aplicação (Modo Claro)"
                        validateStatus={errors.logo_light ? "error" : "success"}
                        help={errors.logo_light?.message}
                    >
                        <Controller
                            name="logo_light"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                                <Dragger
                                    {...field}
                                    showUploadList={false}
                                    customRequest={dummyRequest}
                                    onChange={(e: UploadChangeParam<UploadFile>) =>
                                        onChangeLightLogoImage({
                                            onChangeHookForm: onChange,
                                            file: e.file,
                                        })
                                    }
                                    accept={acceptedImageTypes.toString()}
                                    style={draggerCardStyle}
                                >
                                    {lightLogoImageUrl && typeof lightLogoImageUrl === "string" ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 8 }}>
                                            <img
                                                src={lightLogoImageUrl}
                                                alt="Logo modo claro"
                                                style={{
                                                    maxHeight: 90,
                                                    maxWidth: "80%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                            <Text style={{ fontSize: 12, color: "#1677ff" }}>
                                                Clique para substituir a imagem
                                            </Text>
                                        </div>
                                    ) : (
                                        <div style={{ padding: 12 }}>
                                            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                <InboxOutlined style={{ color: "#1677ff", fontSize: 32 }} />
                                            </p>
                                            <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                                Clique ou arraste o logo claro aqui
                                            </Text>
                                            <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#94a3b8", marginTop: 4 }}>
                                                PNG, JPG, JPEG ou WEBP
                                            </div>
                                        </div>
                                    )}
                                </Dragger>
                            )}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label="Logo da Aplicação (Modo Escuro)"
                        validateStatus={errors.logo_dark ? "error" : "success"}
                        help={errors.logo_dark?.message}
                    >
                        <Controller
                            name="logo_dark"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                                <Dragger
                                    {...field}
                                    showUploadList={false}
                                    customRequest={dummyRequest}
                                    onChange={(e: UploadChangeParam<UploadFile>) =>
                                        onChangeDarkLogoImage({
                                            onChangeHookForm: onChange,
                                            file: e.file,
                                        })
                                    }
                                    accept={acceptedImageTypes.toString()}
                                    style={draggerCardStyle}
                                >
                                    {darkLogoImageUrl && typeof darkLogoImageUrl === "string" ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 8 }}>
                                            <img
                                                src={darkLogoImageUrl}
                                                alt="Logo modo escuro"
                                                style={{
                                                    maxHeight: 90,
                                                    maxWidth: "80%",
                                                    objectFit: "contain",
                                                    backgroundColor: "#1f1f1f",
                                                    borderRadius: 6,
                                                    padding: 4,
                                                }}
                                            />
                                            <Text style={{ fontSize: 12, color: "#1677ff" }}>
                                                Clique para substituir a imagem
                                            </Text>
                                        </div>
                                    ) : (
                                        <div style={{ padding: 12 }}>
                                            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                <InboxOutlined style={{ color: "#722ed1", fontSize: 32 }} />
                                            </p>
                                            <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                                Clique ou arraste o logo escuro aqui
                                            </Text>
                                            <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#94a3b8", marginTop: 4 }}>
                                                PNG, JPG, JPEG ou WEBP
                                            </div>
                                        </div>
                                    )}
                                </Dragger>
                            )}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label="Brasão / Logo Oficial da Prefeitura"
                        validateStatus={errors.logo_county ? "error" : "success"}
                        help={errors.logo_county?.message}
                    >
                        <Controller
                            name="logo_county"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                                <Dragger
                                    {...field}
                                    showUploadList={false}
                                    customRequest={dummyRequest}
                                    onChange={(e: UploadChangeParam<UploadFile>) =>
                                        onChangeLogoCountyImage({
                                            onChangeHookForm: onChange,
                                            file: e.file,
                                        })
                                    }
                                    accept="image/png"
                                    style={draggerCardStyle}
                                >
                                    {logoCountyImageUrl && typeof logoCountyImageUrl === "string" ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 8 }}>
                                            <img
                                                src={logoCountyImageUrl}
                                                alt="Brasão do município"
                                                style={{
                                                    maxHeight: 90,
                                                    maxWidth: "80%",
                                                    objectFit: "contain",
                                                }}
                                            />
                                            <Text style={{ fontSize: 12, color: "#1677ff" }}>
                                                Clique para substituir o brasão
                                            </Text>
                                        </div>
                                    ) : (
                                        <div style={{ padding: 12 }}>
                                            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                <InboxOutlined style={{ color: "#13c2c2", fontSize: 32 }} />
                                            </p>
                                            <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                                Clique ou arraste o brasão oficial (PNG)
                                            </Text>
                                            <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#94a3b8", marginTop: 4 }}>
                                                Formato PNG com fundo transparente recomendado
                                            </div>
                                        </div>
                                    )}
                                </Dragger>
                            )}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label="Imagem de Fundo da Tela de Login"
                        validateStatus={errors.img_background ? "error" : "success"}
                        help={errors.img_background?.message}
                    >
                        <Controller
                            name="img_background"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                                <Dragger
                                    {...field}
                                    showUploadList={false}
                                    customRequest={dummyRequest}
                                    onChange={(e: UploadChangeParam<UploadFile>) =>
                                        onChangeBackgroundImage({
                                            onChangeHookForm: onChange,
                                            file: e.file,
                                        })
                                    }
                                    accept="image/png, image/jpg, image/jpeg, image/webp"
                                    style={draggerCardStyle}
                                >
                                    {backgroundImageUrl && typeof backgroundImageUrl === "string" ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: 8 }}>
                                            <img
                                                src={backgroundImageUrl}
                                                alt="Imagem de fundo"
                                                style={{
                                                    maxHeight: 90,
                                                    maxWidth: "80%",
                                                    objectFit: "cover",
                                                    borderRadius: 6,
                                                }}
                                            />
                                            <Text style={{ fontSize: 12, color: "#1677ff" }}>
                                                Clique para substituir o fundo
                                            </Text>
                                        </div>
                                    ) : (
                                        <div style={{ padding: 12 }}>
                                            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                <InboxOutlined style={{ color: "#fa8c16", fontSize: 32 }} />
                                            </p>
                                            <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                                Clique ou arraste a imagem de fundo
                                            </Text>
                                            <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#94a3b8", marginTop: 4 }}>
                                                PNG, JPG, JPEG ou WEBP
                                            </div>
                                        </div>
                                    )}
                                </Dragger>
                            )}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* Seção 2: Documentos e Regulamentos */}
            <FieldsGroupTitle
                title="Documentos e Regulamentos"
                icon={<FilePdfOutlined style={{ color: "#ff4d4f" }} />}
                subtitle="Arquivos PDF referentes às regras de sorteios e termos de uso do programa."
                showDivider
            />

            <Row gutter={[16, 16]}>
                <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label="Regulamento do Sorteio (PDF)"
                        validateStatus={errors.raffle_rules_file ? "error" : "success"}
                        help={errors.raffle_rules_file?.message}
                    >
                        <Controller
                            name="raffle_rules_file"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                                <Dragger
                                    {...field}
                                    showUploadList={false}
                                    customRequest={dummyRequest}
                                    onChange={(e: UploadChangeParam<UploadFile>) =>
                                        onChangeRaffleRulesFile({
                                            onChangeHookForm: onChange,
                                            file: e.file,
                                        })
                                    }
                                    accept={acceptedFileTypes.toString()}
                                    style={draggerCardStyle}
                                >
                                    {raffleRulesFileName && typeof raffleRulesFileName === "string" ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: 10 }}>
                                            <FilePdfOutlined style={{ fontSize: 30, color: "#ff4d4f" }} />
                                            <Text strong style={{ fontSize: 13 }}>
                                                {raffleRulesFileName}
                                            </Text>
                                            <Text style={{ fontSize: 12, color: "#1677ff" }}>
                                                Clique para substituir o arquivo
                                            </Text>
                                        </div>
                                    ) : (
                                        <div style={{ padding: 12 }}>
                                            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                <InboxOutlined style={{ color: "#ff4d4f", fontSize: 32 }} />
                                            </p>
                                            <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                                Clique ou arraste o PDF do regulamento
                                            </Text>
                                            <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#94a3b8", marginTop: 4 }}>
                                                Apenas arquivos .PDF
                                            </div>
                                        </div>
                                    )}
                                </Dragger>
                            )}
                        />
                    </Form.Item>

                    {uploadedRaffleRulesFile && (
                        <Button
                            type="default"
                            icon={<EyeOutlined />}
                            onClick={() => handleClickViewRaffleRules(uploadedRaffleRulesFile)}
                            style={{
                                width: "100%",
                                marginTop: -8,
                                marginBottom: 12,
                                borderRadius: 6,
                            }}
                        >
                            Visualizar Regulamento Atual
                        </Button>
                    )}
                </Col>

                {/* <Col xs={24} sm={24} md={12} lg={12}>
                    <Form.Item
                        label="Termos de Uso (PDF)"
                        validateStatus={errors.terms_of_use ? "error" : "success"}
                        help={errors.terms_of_use?.message}
                    >
                        <Controller
                            name="terms_of_use"
                            control={control}
                            render={({ field: { value, onChange, ...field } }) => (
                                <Dragger
                                    {...field}
                                    showUploadList={false}
                                    customRequest={dummyRequest}
                                    onChange={(e: UploadChangeParam<UploadFile>) =>
                                        onChangeTermsOfUseFile({
                                            onChangeHookForm: onChange,
                                            file: e.file,
                                        })
                                    }
                                    accept={acceptedFileTypes.toString()}
                                    style={draggerCardStyle}
                                >
                                    {termsOfUseFileName && typeof termsOfUseFileName === "string" ? (
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: 10 }}>
                                            <FilePdfOutlined style={{ fontSize: 30, color: "#1677ff" }} />
                                            <Text strong style={{ fontSize: 13 }}>
                                                {termsOfUseFileName}
                                            </Text>
                                            <Text style={{ fontSize: 12, color: "#1677ff" }}>
                                                Clique para substituir o arquivo
                                            </Text>
                                        </div>
                                    ) : (
                                        <div style={{ padding: 12 }}>
                                            <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                                                <InboxOutlined style={{ color: "#1677ff", fontSize: 32 }} />
                                            </p>
                                            <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                                Clique ou arraste o PDF dos Termos de Uso
                                            </Text>
                                            <div style={{ fontSize: 11.5, color: isDark ? "#8c8c8c" : "#94a3b8", marginTop: 4 }}>
                                                Apenas arquivos .PDF
                                            </div>
                                        </div>
                                    )}
                                </Dragger>
                            )}
                        />
                    </Form.Item>

                    {needsToInformTermsOfUseEffectiveDate && (
                        <Form.Item
                            label="Data de Vigência dos Novos Termos"
                            required
                            validateStatus={errors.terms_of_use_effective_date ? "error" : "success"}
                            help={errors.terms_of_use_effective_date?.message}
                            tooltip="Data a partir da qual os novos Termos de Uso passarão a ser válidos no aplicativo."
                        >
                            <Controller
                                name="terms_of_use_effective_date"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        format={(date) => date.toLocaleDateString("pt-BR")}
                                        value={field.value ? new Date(field.value) : undefined}
                                        onChange={field.onChange}
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    )}

                    {hasTermsOfUse && (
                        <Button
                            type="default"
                            icon={<EyeOutlined />}
                            onClick={handleClickViewTermsOfUse}
                            style={{
                                width: "100%",
                                marginTop: -8,
                                marginBottom: 12,
                                borderRadius: 6,
                            }}
                        >
                            Visualizar Termos de Uso Atual
                        </Button>
                    )}

                    {upcomingTerms && (
                        <UpcomingTermsAlert
                            title="Próximos termos de uso a entrar em vigência"
                            terms={upcomingTerms}
                        />
                    )}
                </Col> */}
            </Row>
        </div>
    );
}
